import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from '../components/Layout';
import CompanyHome from '../pages/CompanyHome';

/**
 * 路由级代码分割。
 *
 * 改动前所有页面打进同一个 bundle，访问首页要连带下载关于页、联系页
 * 以及几万字的法务长文档数据 —— 后者尤其亏：QuQu 与薪资奖励官的
 * 协议/隐私政策合计上千行常量，几乎没有访客会在首屏用到。
 *
 * 只有 Layout 与首页保持静态导入：它们是必经路径，
 * 拆出去反而多一次 chunk 往返，拖慢 LCP。其余全部按需加载。
 */
const About = lazy(() => import('../pages/About'));
const Contact = lazy(() => import('../pages/Contact'));
const FAQ = lazy(() => import('../pages/FAQ'));
const News = lazy(() => import('../pages/News'));
const Products = lazy(() => import('../pages/Products'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const Terms = lazy(() => import('../pages/Terms'));
const SalaryRewardDetail = lazy(() => import('../pages/SalaryRewardDetail'));
const SalaryRewardUserAgreement = lazy(() => import('../pages/SalaryRewardUserAgreement'));
const SalaryRewardPrivacyPolicy = lazy(() => import('../pages/SalaryRewardPrivacyPolicy'));
const DingwuyouDetail = lazy(() => import('../pages/DingwuyouDetail'));
const QuQuDetail = lazy(() => import('../pages/QuQuDetail'));
const QuQuUserAgreement = lazy(() => import('../pages/QuQuUserAgreement'));
const QuQuPrivacyPolicy = lazy(() => import('../pages/QuQuPrivacyPolicy'));
const SalesAgentDetail = lazy(() => import('../pages/SalesAgentDetail'));
const RankingMiniprogramDetail = lazy(() => import('../pages/RankingMiniprogramDetail'));
const AiChat = lazy(() => import('../pages/AiChat'));
const UnderConstruction = lazy(() => import('../components/UnderConstruction'));

/**
 * 加载占位。
 * 刻意不放 spinner：chunk 通常在一两百毫秒内到位，
 * 转圈动画反而会闪一下，比空白更吵。留出屏高只为防止页脚上跳。
 */
const RouteFallback: React.FC = () => (
  <div className="min-h-screen" style={{ background: 'var(--sx-canvas)' }} aria-busy="true" />
);

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/ai" element={<AiChat />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<CompanyHome />} />
          <Route path="about" element={<About />} />
          <Route path="products" element={<Products />} />
          <Route path="products/salary-reward" element={<SalaryRewardDetail />} />
          {/* 下面两条 URL 已写入 App Store 上架信息，路径不可变更 */}
          <Route path="products/salary-reward/user-agreement" element={<SalaryRewardUserAgreement />} />
          <Route path="products/salary-reward/privacy-policy" element={<SalaryRewardPrivacyPolicy />} />
          <Route path="products/dingwuyou" element={<DingwuyouDetail />} />
          <Route path="products/ququ" element={<QuQuDetail />} />
          <Route path="products/ququ/user-agreement" element={<QuQuUserAgreement />} />
          <Route path="products/ququ/privacy-policy" element={<QuQuPrivacyPolicy />} />
          <Route path="products/sales-agent" element={<SalesAgentDetail />} />
          <Route path="products/ranking-miniprogram" element={<RankingMiniprogramDetail />} />
          <Route path="news" element={<News />} />
          <Route path="contact" element={<Contact />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="faq" element={<FAQ />} />

          {/* 遗留路由重定向 */}
          <Route path="ecommerce" element={<Navigate to="/products#tob" replace />} />
          <Route path="solutions" element={<Navigate to="/products#tob" replace />} />
          <Route path="features" element={<Navigate to="/products" replace />} />
          <Route path="reviews" element={<Navigate to="/products" replace />} />
          <Route path="blog" element={<Navigate to="/news" replace />} />

          <Route
            path="*"
            element={
              <UnderConstruction title="页面不存在" message="抱歉，您访问的页面不存在。" />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
